import type { Address, Folder, FolderRole } from '@momomail/shared';
import { language, locale, t } from './i18n';

const pad = (n: number) => String(n).padStart(2, '0');

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Time in the list: today shows only hours:minutes, yesterday shows "昨天" (Yesterday), this year shows month/day, older shows year/month/day. */
export function shortDate(ms: number, now = new Date()): string {
  const d = new Date(ms);
  if (sameDay(d, now)) return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (sameDay(d, y)) return t('昨天');
  const diff = (now.getTime() - ms) / 86_400_000;
  const en = language() !== 'zh';
  if (diff < 6 && diff > 0) return en ? d.toLocaleDateString(locale(), { weekday: 'short' }) : ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][d.getDay()]!; // i18n-ok
  if (d.getFullYear() === now.getFullYear()) return en ? d.toLocaleDateString(locale(), { month: 'short', day: 'numeric' }) : `${d.getMonth() + 1}月${d.getDate()}日`; // i18n-ok
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}

export function longDate(ms: number): string {
  const d = new Date(ms);
  if (language() !== 'zh') return `${d.toLocaleDateString(locale(), { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const week = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][d.getDay()]; // i18n-ok
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 ${week} ${pad(d.getHours())}:${pad(d.getMinutes())}`; // i18n-ok
}

export function timeAgo(ms: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 45) return t('刚刚');
  if (s < 3600) return t('{n} 分钟前', { n: Math.round(s / 60) });
  if (s < 86400) return t('{n} 小时前', { n: Math.round(s / 3600) });
  return shortDate(ms);
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function displayName(a: Address | undefined): string {
  if (!a) return t('(未知)');
  return a.name || a.address.split('@')[0] || a.address || t('(未知)');
}

export const ROLE_NAMES: Record<FolderRole, string> = {
  inbox: '收件箱', // i18n-ok
  drafts: '草稿箱', // i18n-ok
  sent: '已发送', // i18n-ok
  archive: '归档', // i18n-ok
  spam: '垃圾邮件', // i18n-ok
  trash: '已删除', // i18n-ok
};

export function folderName(f: Pick<Folder, 'role' | 'name'>): string {
  return f.role ? t(ROLE_NAMES[f.role]) : f.name;
}

const AVATAR_COLORS = ['#ffd23f', '#1fc98f', '#17b6d6', '#ff8fa3', '#b9a8ff', '#ffb067', '#7ee0c3'];

export function avatarColor(seed: string): string {
  let h = 0;
  for (const ch of seed.toLowerCase()) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}

export function initial(a: Address | undefined): string {
  const name = displayName(a).replace(/^["'“]/, '').trim();
  const first = [...name][0] ?? '?';
  return first.toUpperCase();
}

export function formatAddress(a: Address): string {
  return a.name ? `${a.name} <${a.address}>` : a.address;
}

/** Separator for name lists: the Chinese enumeration comma (、) in Chinese, a comma in English. */
export function listSep(): string {
  return language() === 'zh' ? '、' : ', ';
}
